import { Promotion } from "@src/types/diamondSearchResult";
import { Diamond } from "@src/types/DiamondDetail";
import {
  useAnimatedStyle,
  withTiming,
  Easing,
  SharedValue,
  useSharedValue,
} from "react-native-reanimated";
import { useTheme } from "styled-components/native";
import { ProductCategory } from "@utils/constants";
import { SpaceType } from "@components/atoms/Spacer/types";
import { ShareVia } from "@components/molecules/Share/utils/utils";
import { ExploreStackNavigationProps } from "@navigations/explore-navigator";
import Routes from "@navigations/Routes";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { ShareMenuOptionEnum } from "@utils/product/menuBuilders";
import {
  SearchResultsAndFavoritesEnum,
  SearchResultsScreenModals,
} from "@utils/product/searchResultConstants";
import { RouteProp } from "@react-navigation/native";

export type SearchGridItem = {
  id: number;
  title: string;
  description: string;
  icon: string;
  eventName: string;
  selection: boolean;
};

export type EmptyViewProps = {
  productType: ProductCategory;
  sectionName?: string | null;
  isFilterVisible?: boolean;
  toggleFilterScreen?: any;
};

export type SortingModalProps = {
  isOpen: boolean;
  onClose: any;
  productType: string;
  onApply: any;
  selectedSortingFilters?: Array<string>;
  sectionName?: SearchFilterTypeEnum;
};

export type RequestNowModalProps = {
  isOpen: boolean;
  onClose: any;
  productType: ProductCategory;
  selectOption: (value: string) => void;
  selectedSortingFilters?: Array<string>;
  priceMode: number;
  requestItems?: any[];
};

export type ListViewModalProps = {
  isOpen: boolean;
  onClose: any;
  productType: string;
  onApply: any;
  onReset: any;
  selectedSortingFilters?: Array<string>;
  backgroundColor: string;
  sectionName?: string | null;
};

export type SavedSearchPopupProps = {
  onSavedSearchPopupShowChange: (e: boolean) => void;
  queryParamList: any;
  productType: string;
};

export type PaginationFilterState = {
  selectedViewType: SearchResultsAndFavoritesEnum | string;
  loading: boolean;
  lastPage: boolean;
  totalItems: number;
  masterCheckboxSelected: boolean;
  pageNumber: number;
  itemList: Diamond[];
  combinedList: (Diamond | Promotion)[];
  selectedItems: Diamond[];
};

export type FavDataState = {
  id: number;
  eventType: string;
};

export enum BannerActivityType {
  Login = "login",
  ReturnFromBackground = "return_from_background",
  ProductSwitch = "product_switch",
  NewSearch = "new_search",
}

export function getSecondsOfDay(): number {
  const now = new Date();
  return (
    now.getUTCHours() * 3600 + now.getUTCMinutes() * 60 + now.getUTCSeconds()
  );
}

export const getRemainingTimeForNextAd = (
  lastAdTime: number,
  adDelayTiming: number
): number => {
  if (!lastAdTime) return 0;

  const savedTime = getSecondsOfDay();
  const totalTime = lastAdTime + adDelayTiming;

  if (savedTime < totalTime) {
    return totalTime - savedTime;
  }

  return 0;
};

type UseMasterCheckBoxAnimatedStyleParams = {
  yOffset: SharedValue<number>;
  checkBoxContainerWidth: SharedValue<number>;
  mSelectedViewType: SearchResultsAndFavoritesEnum | string;
};

export const useMasterCheckBoxAnimatedStyle = ({
  yOffset,
  checkBoxContainerWidth,
  mSelectedViewType,
}: UseMasterCheckBoxAnimatedStyleParams) => {
  const theme = useTheme();
  const isListView = mSelectedViewType === SearchResultsAndFavoritesEnum.LIST;
  const isListExpandedView =
    mSelectedViewType === SearchResultsAndFavoritesEnum.LIST_EXPANDED;
  const paddingLeft =
    mSelectedViewType && (isListView || isListExpandedView) ? 10 : 5;
  const paddingRight =
    mSelectedViewType && (isListView || isListExpandedView) ? 10 : 5;
  const isTablet = theme.isLargeScreen || theme.isTablet;
  const checkBoxContainerMarginTop = useSharedValue(
    theme.isMobile ? SpaceType.Basic : SpaceType.Double
  );
  const checkBoxContainerBorderRadius = useSharedValue(8);
  const checkBoxContainerBorderTopWidth = useSharedValue(1);
  const checkBoxContainerPaddingLeft = useSharedValue(paddingLeft);
  const checkBoxContainerPaddingRight = useSharedValue(paddingRight);
  return useAnimatedStyle(() => {
    const isScrolled = yOffset.value > 0;

    return {
      width: isScrolled ? "100%" : checkBoxContainerWidth.value,
      marginTop: withTiming(isScrolled ? 0 : checkBoxContainerMarginTop.value, {
        duration: 250,
        easing: Easing.out(Easing.cubic),
      }),
      borderRadius: isScrolled ? 0 : checkBoxContainerBorderRadius.value,
      borderTopWidth: isScrolled ? checkBoxContainerBorderTopWidth.value : 0,
      borderTopColor: theme["core-03"],
      backgroundColor:
        isListView && isTablet ? theme["core-02"] : theme["core-01"],
      paddingLeft: isScrolled ? 20 : checkBoxContainerPaddingLeft.value,
      paddingRight: isScrolled ? 20 : checkBoxContainerPaddingRight.value,
      overflow: "hidden",
    };
  }, [mSelectedViewType]);
};
export type ActiveSortableOptionItemProps = {
  item: FilterStructure;
  isActive: boolean;
  paramList?: string[];
  invertParam: any;
  removeFromActiveOptions: any;
  onDragStart: any;
  onDragEnd: any;
  activeData: ColumnStructure[];
};

export type ActiveListViewItemProps = {
  item: ColumnStructure;
  hideColumnOptions: any;
  isActive: boolean;
  onDragStart: any;
  onDragEnd: any;
  activeData: ColumnStructure[];
};

export type FlatListProps = {
  paginationFilters: PaginationFilterState;
  scrollHandler: any;
  containerStyle: () => any;
  getNumOfColumns: () => number;
  renderItem: (item: Diamond | Promotion, index: number) => JSX.Element;
  handleLoadMore: () => void;
  renderListHeaderComponent: () => JSX.Element | null;
};

export type SearchResultScreenProps =
  ExploreStackNavigationProps<Routes.SearchResultScreen>;

export const ascending = "ASC";
export const descending = "DESC";

export type FilterStructure = {
  name: string;
  label: string;
  priceModes?: number[];
};

export type ColumnStructure = {
  name: string;
  label: string;
  priceModes?: number[];
  order?: number;
  is_visible?: boolean;
};

export type ModalTypes = {
  name: SearchResultsScreenModals | null;
  selectedOptions: ShareVia | null;
  shareMenuOption: ShareMenuOptionEnum | null;
  openBulkDialog: boolean;
  openOrderCartDialog: boolean;
  isBuyNow?: boolean;
  orderCartItem?: Diamond | null;
};

export type SearchResultNavigationProps = {
  route: RouteProp<any>;
  navigation: any;
  sectionName: any;
  productType: ProductCategory;
  toggleFilterScreen: () => any;
  isFilterVisible: boolean;
  skipFilterScreenForWeb: boolean;
  saved_searches?: any;
  deepLinkNonce?: string;
  collection?: string | string[];
  showFilter?: () => void;
  fromNewFilterScreen?: boolean;
  enterSecondFlow?: boolean;
  fromResultScreen?: boolean;
  fromBarcodeScanner?: boolean;
};

export type ItemDetailsTypes = {
  mItem: Diamond | undefined;
  itemIdArray: number[];
};
